/**
 * @class Destination
 * @classdesc Class definition for record type
 * @param {OBJECT} record - The record from the states or countries objects
 * @example const destination = Destination.initByName("Germany");
 * 
 * @property {STRING} _name - The name of the destination
 * @property {STRING} _abv - The abbreviation of the destination
 * @property {BOOLEAN} _selected - Selected property from copying it over from the destination select
 * @property {BOOLEAN} _isState - If the destination is a state
 * @property {STRING} _flag - The flag icon associated to the destination
 */
export default class Destination {
    _name;
    _abv;
    _selected;
    _isState;
    _flag;

    constructor(record) {
        this.name = record.name;
        this.abv = record.abv;
        this.selected = record.selected;
        this.isState = record.isState;
        this.flag = record.flag;
    }

    /**
     * @function name
     * @instance
     * @param {STRING} value - The name to be set
     * @description Setter for the name property of the destination
     * @memberof Destination
     */
    set name(value) { this._name = value; }

    /**
     * @function abv
     * @instance
     * @param {STRING} value - The abbreviation of the destination name
     * @description Setter for the abv property of the destination
     * @memberof Destination
     */
    set abv(value) { this._abv = value; }

    /**
     * @function selected
     * @instance
     * @param {BOOLEAN} value - The selected value
     * @description Setter for the selected property of the destination
     * @memberof Destination
     */
    set selected(value) { this._selected = value; }

    /**
     * @function isState
     * @instance
     * @param {BOOLEAN} value - The value to be set for the isState property
     * @description Setter for the isState property of the destination
     * @memberof Destination
     */
    set isState(value) { this._isState = value; }

    /**
     * @function flag
     * @instance
     * @param {STRING} value - The value to be set for the flag property
     * @description Setter for the flag property of the destination
     * @memberof Destination
     */
    set flag(value) { this._flag = value; }

    /**
     * @function name
     * @instance
     * @returns {STRING}
     * @description Getter for the name property
     * @memberof Destination
     */
    get name() { return this._name; }

    /**
     * @function abv
     * @instance
     * @returns {STRING}
     * @description Getter for the abv property
     * @memberof Destination
     */
    get abv() { return this._abv; }

    /**
     * @function selected
     * @instance
     * @returns {BOOLEAN}
     * @description Getter for the selected property
     * @memberof Destination
     */
    get selected() { return this._selected; }

    /**
     * @function isState
     * @instance
     * @returns {BOOLEAN}
     * @description Getter for the isState property
     * @memberof Destination
     */
    get isState() { return this._isState; }

    /**
     * @function flag
     * @instance
     * @returns {STRING}
     * @description Getter for the flag property
     * @memberof Destination
     */
    get flag() { return this._flag; }

    /**
     * @function initByName
     * @property {STRING} name - The name of the destination to get
     * @returns {Destination}
     * @static
     * @description - Get the destination by passing the name
     * @memberof Destination
     */
    static initByName(name) {
        const record = Destination.destinations.find(item => item.name === name);
        return new Destination(record);
    }

    /**
     * @function initByAbbreviation
     * @property {STRING} abbr - The abbreviation of the destination to get
     * @returns {Destination}
     * @static
     * @description - Get the destination by passing the abbreviation
     * @memberof Destination
     */
    static initByAbbreviation(abbr) {
        const record = Destination.destinations.find(item => item.abv === abbr);
        return new Destination(record);
    }

    /**
     * @function getFlagByName
     * @property {STRING} name - The name of the destination of the flag to get
     * @returns {STRING}
     * @static
     * @description - Get the flag by destination
     * @memberof Destination
     */
    static getFlagByName(name) {
        return Destination.destinations.find(item => item.name === name).flag;
    }

    /**
     * @function destinations
     * @static
     * @returns {ARRAY}
     * @description Getters to return an array of all destinations
     * @memberof Destinations
     */
    static get destinations() {
        return [ ...Destination.countries, ...Destination.states ];
    }

    /**
     * @function states
     * @static
     * @returns {ARRAY}
     * @description Getter to return all the states
     * @memberof Destination
     */
    static get states() {
        return [{ "name": "Alabama", "abv": "AL", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Alaska", "abv": "AK", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Arizona", "abv": "AZ", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Arkansas", "abv": "AR", "selected":false, "isState":true, "flag": ""}, 
        { "name": "California", "abv": "CA", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Colorado", "abv": "CO", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Connecticut", "abv": "CT", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Delaware", "abv": "DE", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Florida", "abv": "FL", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Georgia", "abv": "GA", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Hawaii", "abv": "HI", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Idaho", "abv": "ID", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Illinois", "abv": "IL", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Indiana", "abv": "IN", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Iowa", "abv": "IA", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Kansas", "abv": "KS", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Kentucky", "abv": "KY", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Louisiana", "abv": "LA", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Maine", "abv": "ME", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Maryland", "abv": "MD", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Massachusetts", "abv": "MA", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Michigan", "abv": "MI", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Minnesota", "abv": "MN", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Mississippi", "abv": "MS", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Missouri", "abv": "MO", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Montana", "abv": "MT", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Nebraska", "abv": "NE", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Nevada", "abv": "NV", "selected":false, "isState":true, "flag": ""}, 
        { "name": "New Hampshire", "abv": "NH", "selected":false, "isState":true, "flag": ""}, 
        { "name": "New Jersey", "abv": "NJ", "selected":false, "isState":true, "flag": ""}, 
        { "name": "New Mexico", "abv": "NM", "selected":false, "isState":true, "flag": ""}, 
        { "name": "New York", "abv": "NY", "selected":false, "isState":true, "flag": ""}, 
        { "name": "North Carolina", "abv": "NC", "selected":false, "isState":true, "flag": ""}, 
        { "name": "North Dakota", "abv": "ND", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Ohio", "abv": "OH", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Oklahoma", "abv": "OK", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Oregon", "abv": "OR", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Pennsylvania", "abv": "PA", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Rhode Island", "abv": "RI", "selected":false, "isState":true, "flag": ""}, 
        { "name": "South Carolina", "abv": "SC", "selected":false, "isState":true, "flag": ""}, 
        { "name": "South Dakota", "abv": "SD", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Tennessee", "abv": "TN", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Texas", "abv": "TX", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Utah", "abv": "UT", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Vermont", "abv": "VT", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Virginia", "abv": "VA", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Washington", "abv": "WA", "selected":false, "isState":true, "flag": ""}, 
        { "name": "West Virginia", "abv": "WV", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Wisconsin", "abv": "WI", "selected":false, "isState":true, "flag": ""}, 
        { "name": "Wyoming", "abv": "WY", "selected":false, "isState":true, "flag": "" }];
    }

    /**
     * @function countries
     * @static
     * @returns {ARRAY}
     * @description Getter to return all the countries
     * @memberof Destination
     */
    static get countries() {
        return [
            { "name": "Afghanistan", "abv": "AF", "selected":false, "isState":false, "flag": ""},
            { "name": "Aland Islands", "abv": "AX", "selected":false, "isState":false, "flag": ""},
            { "name": "Albania", "abv": "AL", "selected":false, "isState":false, "flag": ""},
            { "name": "Algeria", "abv": "DZ", "selected":false, "isState":false, "flag": ""},
            { "name": "American Samoa", "abv": "AS", "selected":false, "isState":false, "flag": ""},
            { "name": "AndorrA", "abv": "AD", "selected":false, "isState":false, "flag": ""},
            { "name": "Angola", "abv": "AO", "selected":false, "isState":false, "flag": ""},
            { "name": "Anguilla", "abv": "AI", "selected":false, "isState":false, "flag": ""},
            { "name": "Antarctica", "abv": "AQ", "selected":false, "isState":false, "flag": ""},
            { "name": "Antigua and Barbuda", "abv": "AG", "selected":false, "isState":false, "flag": ""},
            { "name": "Argentina", "abv": "AR", "selected":false, "isState":false, "flag": ""},
            { "name": "Armenia", "abv": "AM", "selected":false, "isState":false, "flag": ""},
            { "name": "Aruba", "abv": "AW", "selected":false, "isState":false, "flag": ""},
            { "name": "Australia", "abv": "AU", "selected":false, "isState":false, "flag": "AustraliaFlagIcon"},
            { "name": "Austria", "abv": "AT", "selected":false, "isState":false, "flag": ""},
            { "name": "Azerbaijan", "abv": "AZ", "selected":false, "isState":false, "flag": ""},
            { "name": "Bahamas", "abv": "BS", "selected":false, "isState":false, "flag": ""},
            { "name": "Bahrain", "abv": "BH", "selected":false, "isState":false, "flag": ""},
            { "name": "Bangladesh", "abv": "BD", "selected":false, "isState":false, "flag": ""},
            { "name": "Barbados", "abv": "BB", "selected":false, "isState":false, "flag": ""},
            { "name": "Belarus", "abv": "BY", "selected":false, "isState":false, "flag": ""},
            { "name": "Belgium", "abv": "BE", "selected":false, "isState":false, "flag": ""},
            { "name": "Belize", "abv": "BZ", "selected":false, "isState":false, "flag": ""},
            { "name": "Benin", "abv": "BJ", "selected":false, "isState":false, "flag": ""},
            { "name": "Bermuda", "abv": "BM", "selected":false, "isState":false, "flag": ""},
            { "name": "Bhutan", "abv": "BT", "selected":false, "isState":false, "flag": ""},
            { "name": "Bolivia", "abv": "BO", "selected":false, "isState":false, "flag": ""},
            { "name": "Bosnia and Herzegovina", "abv": "BA", "selected":false, "isState":false, "flag": ""},
            { "name": "Botswana", "abv": "BW", "selected":false, "isState":false, "flag": ""},
            { "name": "Bouvet Island", "abv": "BV", "selected":false, "isState":false, "flag": ""},
            { "name": "Brazil", "abv": "BR", "selected":false, "isState":false, "flag": ""},
            { "name": "British Indian Ocean Territory", "abv": "IO", "selected":false, "isState":false, "flag": ""},
            { "name": "Brunei Darussalam", "abv": "BN", "selected":false, "isState":false, "flag": ""},
            { "name": "Bulgaria", "abv": "BG", "selected":false, "isState":false, "flag": ""},
            { "name": "Burkina Faso", "abv": "BF", "selected":false, "isState":false, "flag": ""},
            { "name": "Burundi", "abv": "BI", "selected":false, "isState":false, "flag": ""},
            { "name": "Cambodia", "abv": "KH", "selected":false, "isState":false, "flag": ""},
            { "name": "Cameroon", "abv": "CM", "selected":false, "isState":false, "flag": ""},
            { "name": "Canada", "abv": "CA", "selected":false, "isState":false, "flag": ""},
            { "name": "Cape Verde", "abv": "CV", "selected":false, "isState":false, "flag": ""},
            { "name": "Cayman Islands", "abv": "KY", "selected":false, "isState":false, "flag": ""},
            { "name": "Central African Republic", "abv": "CF", "selected":false, "isState":false, "flag": ""},
            { "name": "Chad", "abv": "TD", "selected":false, "isState":false, "flag": ""},
            { "name": "Chile", "abv": "CL", "selected":false, "isState":false, "flag": ""},
            { "name": "China", "abv": "CN", "selected":false, "isState":false, "flag": ""},
            { "name": "Christmas Island", "abv": "CX", "selected":false, "isState":false, "flag": ""},
            { "name": "Cocos (Keeling) Islands", "abv": "CC", "selected":false, "isState":false, "flag": ""},
            { "name": "Colombia", "abv": "CO", "selected":false, "isState":false, "flag": ""},
            { "name": "Comoros", "abv": "KM", "selected":false, "isState":false, "flag": ""},
            { "name": "Congo", "abv": "CG", "selected":false, "isState":false, "flag": ""},
            { "name": "Congo, The Democratic Republic of the", "abv": "CD", "selected":false, "isState":false, "flag": ""},
            { "name": "Cook Islands", "abv": "CK", "selected":false, "isState":false, "flag": ""},
            { "name": "Costa Rica", "abv": "CR", "selected":false, "isState":false, "flag": ""},
            { "name": "Cote D'Ivoire", "abv": "CI", "selected":false, "isState":false, "flag": ""},
            { "name": "Croatia", "abv": "HR", "selected":false, "isState":false, "flag": ""},
            { "name": "Cuba", "abv": "CU", "selected":false, "isState":false, "flag": ""},
            { "name": "Cyprus", "abv": "CY", "selected":false, "isState":false, "flag": ""},
            { "name": "Czech Republic", "abv": "CZ", "selected":false, "isState":false, "flag": ""},
            { "name": "Denmark", "abv": "DK", "selected":false, "isState":false, "flag": ""},
            { "name": "Djibouti", "abv": "DJ", "selected":false, "isState":false, "flag": ""},
            { "name": "Dominica", "abv": "DM", "selected":false, "isState":false, "flag": ""},
            { "name": "Dominican Republic", "abv": "DO", "selected":false, "isState":false, "flag": ""},
            { "name": "Ecuador", "abv": "EC", "selected":false, "isState":false, "flag": ""},
            { "name": "Egypt", "abv": "EG", "selected":false, "isState":false, "flag": ""},
            { "name": "El Salvador", "abv": "SV", "selected":false, "isState":false, "flag": ""},
            { "name": "Equatorial Guinea", "abv": "GQ", "selected":false, "isState":false, "flag": ""},
            { "name": "Eritrea", "abv": "ER", "selected":false, "isState":false, "flag": ""},
            { "name": "Estonia", "abv": "EE", "selected":false, "isState":false, "flag": ""},
            { "name": "Ethiopia", "abv": "ET", "selected":false, "isState":false, "flag": ""},
            { "name": "Falkland Islands (Malvinas)", "abv": "FK", "selected":false, "isState":false, "flag": ""},
            { "name": "Faroe Islands", "abv": "FO", "selected":false, "isState":false, "flag": ""},
            { "name": "Fiji", "abv": "FJ", "selected":false, "isState":false, "flag": ""},
            { "name": "Finland", "abv": "FI", "selected":false, "isState":false, "flag": ""},
            { "name": "France", "abv": "FR", "selected":false, "isState":false, "flag": ""},
            { "name": "French Guiana", "abv": "GF", "selected":false, "isState":false, "flag": ""},
            { "name": "French Polynesia", "abv": "PF", "selected":false, "isState":false, "flag": ""},
            { "name": "French Southern Territories", "abv": "TF", "selected":false, "isState":false, "flag": ""},
            { "name": "Gabon", "abv": "GA", "selected":false, "isState":false, "flag": ""},
            { "name": "Gambia", "abv": "GM", "selected":false, "isState":false, "flag": ""},
            { "name": "Georgia", "abv": "GE", "selected":false, "isState":false, "flag": ""},
            { "name": "Germany", "abv": "DE", "selected":false, "isState":false, "flag": "GermanyFlagIcon"},
            { "name": "Ghana", "abv": "GH", "selected":false, "isState":false, "flag": ""},
            { "name": "Gibraltar", "abv": "GI", "selected":false, "isState":false, "flag": ""},
            { "name": "Greece", "abv": "GR", "selected":false, "isState":false, "flag": ""},
            { "name": "Greenland", "abv": "GL", "selected":false, "isState":false, "flag": ""},
            { "name": "Grenada", "abv": "GD", "selected":false, "isState":false, "flag": ""},
            { "name": "Guadeloupe", "abv": "GP", "selected":false, "isState":false, "flag": ""},
            { "name": "Guam", "abv": "GU", "selected":false, "isState":false, "flag": ""},
            { "name": "Guatemala", "abv": "GT", "selected":false, "isState":false, "flag": ""},
            { "name": "Guernsey", "abv": "GG", "selected":false, "isState":false, "flag": ""},
            { "name": "Guinea", "abv": "GN", "selected":false, "isState":false, "flag": ""},
            { "name": "Guinea-Bissau", "abv": "GW", "selected":false, "isState":false, "flag": ""},
            { "name": "Guyana", "abv": "GY", "selected":false, "isState":false, "flag": ""},
            { "name": "Haiti", "abv": "HT", "selected":false, "isState":false, "flag": ""},
            { "name": "Heard Island and Mcdonald Islands", "abv": "HM", "selected":false, "isState":false, "flag": ""},
            { "name": "Holy See (Vatican City State)", "abv": "VA", "selected":false, "isState":false, "flag": ""},
            { "name": "Honduras", "abv": "HN", "selected":false, "isState":false, "flag": ""},
            { "name": "Hong Kong", "abv": "HK", "selected":false, "isState":false, "flag": ""},
            { "name": "Hungary", "abv": "HU", "selected":false, "isState":false, "flag": ""},
            { "name": "Iceland", "abv": "IS", "selected":false, "isState":false, "flag": ""},
            { "name": "India", "abv": "IN", "selected":false, "isState":false, "flag": "IndiaFlagIcon"},
            { "name": "Indonesia", "abv": "ID", "selected":false, "isState":false, "flag": ""},
            { "name": "Iran, Islamic Republic Of", "abv": "IR", "selected":false, "isState":false, "flag": ""},
            { "name": "Iraq", "abv": "IQ", "selected":false, "isState":false, "flag": ""},
            { "name": "Ireland", "abv": "IE", "selected":false, "isState":false, "flag": ""},
            { "name": "Isle of Man", "abv": "IM", "selected":false, "isState":false, "flag": ""},
            { "name": "Israel", "abv": "IL", "selected":false, "isState":false, "flag": ""},
            { "name": "Italy", "abv": "IT", "selected":false, "isState":false, "flag": ""},
            { "name": "Jamaica", "abv": "JM", "selected":false, "isState":false, "flag": ""},
            { "name": "Japan", "abv": "JP", "selected":false, "isState":false, "flag": ""},
            { "name": "Jersey", "abv": "JE", "selected":false, "isState":false, "flag": ""},
            { "name": "Jordan", "abv": "JO", "selected":false, "isState":false, "flag": ""},
            { "name": "Kazakhstan", "abv": "KZ", "selected":false, "isState":false, "flag": ""},
            { "name": "Kenya", "abv": "KE", "selected":false, "isState":false, "flag": ""},
            { "name": "Kiribati", "abv": "KI", "selected":false, "isState":false, "flag": ""},
            { "name": "Korea, Democratic People'S Republic of", "abv": "KP", "selected":false, "isState":false, "flag": ""},
            { "name": "Korea, Republic of", "abv": "KR", "selected":false, "isState":false, "flag": ""},
            { "name": "Kuwait", "abv": "KW", "selected":false, "isState":false, "flag": ""},
            { "name": "Kyrgyzstan", "abv": "KG", "selected":false, "isState":false, "flag": ""},
            { "name": "Lao People'S Democratic Republic", "abv": "LA", "selected":false, "isState":false, "flag": ""},
            { "name": "Latvia", "abv": "LV", "selected":false, "isState":false, "flag": ""},
            { "name": "Lebanon", "abv": "LB", "selected":false, "isState":false, "flag": ""},
            { "name": "Lesotho", "abv": "LS", "selected":false, "isState":false, "flag": ""},
            { "name": "Liberia", "abv": "LR", "selected":false, "isState":false, "flag": ""},
            { "name": "Libyan Arab Jamahiriya", "abv": "LY", "selected":false, "isState":false, "flag": ""},
            { "name": "Liechtenstein", "abv": "LI", "selected":false, "isState":false, "flag": ""},
            { "name": "Lithuania", "abv": "LT", "selected":false, "isState":false, "flag": ""},
            { "name": "Luxembourg", "abv": "LU", "selected":false, "isState":false, "flag": ""},
            { "name": "Macao", "abv": "MO", "selected":false, "isState":false, "flag": ""},
            { "name": "Macedonia, The Former Yugoslav Republic of", "abv": "MK", "selected":false, "isState":false, "flag": ""},
            { "name": "Madagascar", "abv": "MG", "selected":false, "isState":false, "flag": ""},
            { "name": "Malawi", "abv": "MW", "selected":false, "isState":false, "flag": ""},
            { "name": "Malaysia", "abv": "MY", "selected":false, "isState":false, "flag": ""},
            { "name": "Maldives", "abv": "MV", "selected":false, "isState":false, "flag": ""},
            { "name": "Mali", "abv": "ML", "selected":false, "isState":false, "flag": ""},
            { "name": "Malta", "abv": "MT", "selected":false, "isState":false, "flag": ""},
            { "name": "Marshall Islands", "abv": "MH", "selected":false, "isState":false, "flag": ""},
            { "name": "Martinique", "abv": "MQ", "selected":false, "isState":false, "flag": ""},
            { "name": "Mauritania", "abv": "MR", "selected":false, "isState":false, "flag": ""},
            { "name": "Mauritius", "abv": "MU", "selected":false, "isState":false, "flag": ""},
            { "name": "Mayotte", "abv": "YT", "selected":false, "isState":false, "flag": ""},
            { "name": "Mexico", "abv": "MX", "selected":false, "isState":false, "flag": ""},
            { "name": "Micronesia, Federated States of", "abv": "FM", "selected":false, "isState":false, "flag": ""},
            { "name": "Moldova, Republic of", "abv": "MD", "selected":false, "isState":false, "flag": ""},
            { "name": "Monaco", "abv": "MC", "selected":false, "isState":false, "flag": ""},
            { "name": "Mongolia", "abv": "MN", "selected":false, "isState":false, "flag": ""},
            { "name": "Montenegro", "abv": "ME", "selected":false, "isState":false, "flag": ""},
            { "name": "Montserrat", "abv": "MS", "selected":false, "isState":false, "flag": ""},
            { "name": "Morocco", "abv": "MA", "selected":false, "isState":false, "flag": ""},
            { "name": "Mozambique", "abv": "MZ", "selected":false, "isState":false, "flag": ""},
            { "name": "Myanmar", "abv": "MM", "selected":false, "isState":false, "flag": ""},
            { "name": "Namibia", "abv": "NA", "selected":false, "isState":false, "flag": ""},
            { "name": "Nauru", "abv": "NR", "selected":false, "isState":false, "flag": ""},
            { "name": "Nepal", "abv": "NP", "selected":false, "isState":false, "flag": ""},
            { "name": "Netherlands", "abv": "NL", "selected":false, "isState":false, "flag": ""},
            { "name": "Netherlands Antilles", "abv": "AN", "selected":false, "isState":false, "flag": ""},
            { "name": "New Caledonia", "abv": "NC", "selected":false, "isState":false, "flag": ""},
            { "name": "New Zealand", "abv": "NZ", "selected":false, "isState":false, "flag": ""},
            { "name": "Nicaragua", "abv": "NI", "selected":false, "isState":false, "flag": ""},
            { "name": "Niger", "abv": "NE", "selected":false, "isState":false, "flag": ""},
            { "name": "Nigeria", "abv": "NG", "selected":false, "isState":false, "flag": ""},
            { "name": "Niue", "abv": "NU", "selected":false, "isState":false, "flag": ""},
            { "name": "Norfolk Island", "abv": "NF", "selected":false, "isState":false, "flag": ""},
            { "name": "Northern Mariana Islands", "abv": "MP", "selected":false, "isState":false, "flag": ""},
            { "name": "Norway", "abv": "NO", "selected":false, "isState":false, "flag": ""},
            { "name": "Oman", "abv": "OM", "selected":false, "isState":false, "flag": ""},
            { "name": "Pakistan", "abv": "PK", "selected":false, "isState":false, "flag": ""},
            { "name": "Palau", "abv": "PW", "selected":false, "isState":false, "flag": ""},
            { "name": "Palestinian Territory, Occupied", "abv": "PS", "selected":false, "isState":false, "flag": ""},
            { "name": "Panama", "abv": "PA", "selected":false, "isState":false, "flag": ""},
            { "name": "Papua New Guinea", "abv": "PG", "selected":false, "isState":false, "flag": ""},
            { "name": "Paraguay", "abv": "PY", "selected":false, "isState":false, "flag": ""},
            { "name": "Peru", "abv": "PE", "selected":false, "isState":false, "flag": ""},
            { "name": "Philippines", "abv": "PH", "selected":false, "isState":false, "flag": ""},
            { "name": "Pitcairn", "abv": "PN", "selected":false, "isState":false, "flag": ""},
            { "name": "Poland", "abv": "PL", "selected":false, "isState":false, "flag": ""},
            { "name": "Portugal", "abv": "PT", "selected":false, "isState":false, "flag": ""},
            { "name": "Puerto Rico", "abv": "PR", "selected":false, "isState":false, "flag": ""},
            { "name": "Qatar", "abv": "QA", "selected":false, "isState":false, "flag": ""},
            { "name": "Reunion", "abv": "RE", "selected":false, "isState":false, "flag": ""},
            { "name": "Romania", "abv": "RO", "selected":false, "isState":false, "flag": ""},
            { "name": "Russian Federation", "abv": "RU", "selected":false, "isState":false, "flag": ""},
            { "name": "RWANDA", "abv": "RW", "selected":false, "isState":false, "flag": ""},
            { "name": "Saint Helena", "abv": "SH", "selected":false, "isState":false, "flag": ""},
            { "name": "Saint Kitts and Nevis", "abv": "KN", "selected":false, "isState":false, "flag": ""},
            { "name": "Saint Lucia", "abv": "LC", "selected":false, "isState":false, "flag": ""},
            { "name": "Saint Pierre and Miquelon", "abv": "PM", "selected":false, "isState":false, "flag": ""},
            { "name": "Saint Vincent and the Grenadines", "abv": "VC", "selected":false, "isState":false, "flag": ""},
            { "name": "Samoa", "abv": "WS", "selected":false, "isState":false, "flag": ""},
            { "name": "San Marino", "abv": "SM", "selected":false, "isState":false, "flag": ""},
            { "name": "Sao Tome and Principe", "abv": "ST", "selected":false, "isState":false, "flag": ""},
            { "name": "Saudi Arabia", "abv": "SA", "selected":false, "isState":false, "flag": ""},
            { "name": "Senegal", "abv": "SN", "selected":false, "isState":false, "flag": ""},
            { "name": "Serbia", "abv": "RS", "selected":false, "isState":false, "flag": ""},
            { "name": "Seychelles", "abv": "SC", "selected":false, "isState":false, "flag": ""},
            { "name": "Sierra Leone", "abv": "SL", "selected":false, "isState":false, "flag": ""},
            { "name": "Singapore", "abv": "SG", "selected":false, "isState":false, "flag": ""},
            { "name": "Slovakia", "abv": "SK", "selected":false, "isState":false, "flag": ""},
            { "name": "Slovenia", "abv": "SI", "selected":false, "isState":false, "flag": ""},
            { "name": "Solomon Islands", "abv": "SB", "selected":false, "isState":false, "flag": ""},
            { "name": "Somalia", "abv": "SO", "selected":false, "isState":false, "flag": ""},
            { "name": "South Africa", "abv": "ZA", "selected":false, "isState":false, "flag": ""},
            { "name": "South Georgia and the South Sandwich Islands", "abv": "GS", "selected":false, "isState":false, "flag": ""},
            { "name": "Spain", "abv": "ES", "selected":false, "isState":false, "flag": ""},
            { "name": "Sri Lanka", "abv": "LK", "selected":false, "isState":false, "flag": ""},
            { "name": "Sudan", "abv": "SD", "selected":false, "isState":false, "flag": ""},
            { "name": "Suriname", "abv": "SR", "selected":false, "isState":false, "flag": ""},
            { "name": "Svalbard and Jan Mayen", "abv": "SJ", "selected":false, "isState":false, "flag": ""},
            { "name": "Swaziland", "abv": "SZ", "selected":false, "isState":false, "flag": ""},
            { "name": "Sweden", "abv": "SE", "selected":false, "isState":false, "flag": ""},
            { "name": "Switzerland", "abv": "CH", "selected":false, "isState":false, "flag": ""},
            { "name": "Syrian Arab Republic", "abv": "SY", "selected":false, "isState":false, "flag": ""},
            { "name": "Taiwan, Province of China", "abv": "TW", "selected":false, "isState":false, "flag": ""},
            { "name": "Tajikistan", "abv": "TJ", "selected":false, "isState":false, "flag": ""},
            { "name": "Tanzania, United Republic of", "abv": "TZ", "selected":false, "isState":false, "flag": ""},
            { "name": "Thailand", "abv": "TH", "selected":false, "isState":false, "flag": ""},
            { "name": "Timor-Leste", "abv": "TL", "selected":false, "isState":false, "flag": ""},
            { "name": "Togo", "abv": "TG", "selected":false, "isState":false, "flag": ""},
            { "name": "Tokelau", "abv": "TK", "selected":false, "isState":false, "flag": ""},
            { "name": "Tonga", "abv": "TO", "selected":false, "isState":false, "flag": ""},
            { "name": "Trinidad and Tobago", "abv": "TT", "selected":false, "isState":false, "flag": ""},
            { "name": "Tunisia", "abv": "TN", "selected":false, "isState":false, "flag": ""},
            { "name": "Turkey", "abv": "TR", "selected":false, "isState":false, "flag": ""},
            { "name": "Turkmenistan", "abv": "TM", "selected":false, "isState":false, "flag": ""},
            { "name": "Turks and Caicos Islands", "abv": "TC", "selected":false, "isState":false, "flag": ""},
            { "name": "Tuvalu", "abv": "TV", "selected":false, "isState":false, "flag": ""},
            { "name": "Uganda", "abv": "UG", "selected":false, "isState":false, "flag": ""},
            { "name": "Ukraine", "abv": "UA", "selected":false, "isState":false, "flag": ""},
            { "name": "United Arab Emirates", "abv": "AE", "selected":false, "isState":false, "flag": ""},
            { "name": "United Kingdom", "abv": "GB", "selected":false, "isState":false, "flag": ""},
            { "name": "United States", "abv": "US", "selected":false, "isState":false, "flag": "UnitedStatesFlagIcon"},
            { "name": "United States Minor Outlying Islands", "abv": "UM", "selected":false, "isState":false, "flag": ""},
            { "name": "Uruguay", "abv": "UY", "selected":false, "isState":false, "flag": ""},
            { "name": "Uzbekistan", "abv": "UZ", "selected":false, "isState":false, "flag": ""},
            { "name": "Vanuatu", "abv": "VU", "selected":false, "isState":false, "flag": ""},
            { "name": "Venezuela", "abv": "VE", "selected":false, "isState":false, "flag": ""},
            { "name": "Viet Nam", "abv": "VN", "selected":false, "isState":false, "flag": ""},
            { "name": "Virgin Islands, British", "abv": "VG", "selected":false, "isState":false, "flag": ""},
            { "name": "Virgin Islands, U.S.", "abv": "VI", "selected":false, "isState":false, "flag": ""},
            { "name": "Wallis and Futuna", "abv": "WF", "selected":false, "isState":false, "flag": ""},
            { "name": "Western Sahara", "abv": "EH", "selected":false, "isState":false, "flag": ""},
            { "name": "Yemen", "abv": "YE", "selected":false, "isState":false, "flag": ""},
            { "name": "Zambia", "abv": "ZM", "selected":false, "isState":false, "flag": ""},
            { "name": "Zimbabwe", "abv": "ZW", "selected":false, "isState":false, "flag": "" }
        ]
    }
}